import { useCallback, useEffect, useReducer, useRef } from "react";
import { NotistackServices, TeamLeaderServices, TeamConversationDTO, TeamMessageDTO, type MessageAttachment } from "shared/services";
import type { SSEConfirmationMessage, SSEConfirmationRequired, SSEToolCall, StreamCallbacks } from "shared/services/AssistantServices";
import { extractTextFromMessage, HandleError, isLegacyAssistantMessageDTO } from "shared/utils";
import { useStreamBuffer } from "./useStreamBuffer";

// ── Types ─────────────────────────────────────────────────────────

export interface ChatMessage {
	id: string;
	content: string;
	senderType: "user" | "leader" | "member" | "assistente" | "confirmation";
	senderRef?: string;
	pending?: boolean;
	error?: boolean;
	errorMessage?: string;
	thinking?: string;
	createdAt: string;
	/** True enquanto esta mensagem está recebendo tokens de streaming. */
	isStreaming?: boolean;
	/** Metadados extras (usado por mensagens de confirmação). */
	metadata?: Record<string, any>;
	/** EK-408 polish: anexos persistidos exibidos no histórico. */
	attachments?: import("shared/types/MessageContracts").PublicMessageAttachmentDTO[];
}

export interface PendingConfirmation {
	confirmationId: string;
	toolName: string;
	description: string;
	arguments?: Record<string, any>;
}

export interface LogEntry {
	time: string;
	sender: string;
	senderColor: string;
	text: string;
}

// ── Helpers ───────────────────────────────────────────────────────

/** EK-408: ver useAssistantMessages.filesToAttachments — mesma estratégia. */
async function filesToAttachments(workspaceId: string, files: File[]): Promise<MessageAttachment[]> {
	const { WorkspaceServices } = await import("shared/services");
	return Promise.all(
		files.map(async (file) => {
			const uploaded = await WorkspaceServices.uploadChatAttachment(workspaceId, file);
			return { attachmentId: uploaded.id, filename: uploaded.filename, mimeType: uploaded.mimeType };
		}),
	);
}

export function nowTime(): string {
	return new Date().toLocaleTimeString("pt-BR", { hour: "2-digit", minute: "2-digit", second: "2-digit" });
}

function mapMessageDTO(m: TeamMessageDTO): ChatMessage | null {
	// Filtrar respostas de confirmação (mensagens internas)
	if (m.metadata?.isConfirmationResponse) return null;
	return {
		id: `msg-${m.id}`,
		content: m.content,
		senderType: m.senderType as ChatMessage["senderType"],
		senderRef: m.senderRef,
		createdAt: m.createdAt,
		metadata: m.senderType === "confirmation" ? m.metadata : undefined,
		// EK-408 polish: anexos persistidos vêm anexados a cada mensagem
		// pelo backend (AIAbstractEngine.getConversation).
		attachments: (m as any).attachments ?? undefined,
	};
}

// ── State & Reducer ───────────────────────────────────────────────

interface ChatState {
	conversations: TeamConversationDTO[];
	conversationId: string | null;
	messages: ChatMessage[];
	isStreaming: boolean;
	loadingChat: boolean;
	activeTools: Map<string, string>;
	pendingConfirmation: PendingConfirmation | null;
	logs: LogEntry[];
}

const INITIAL_STATE: ChatState = {
	conversations: [],
	conversationId: null,
	messages: [],
	isStreaming: false,
	loadingChat: false,
	activeTools: new Map(),
	pendingConfirmation: null,
	logs: [],
};

type ChatAction =
	// Lifecycle
	| { type: "RESET" }
	// Conversations
	| { type: "CONVERSATIONS_LOADED"; data: TeamConversationDTO[]; latestId: string | null }
	| { type: "CONVERSATIONS_LOAD_FAILED" }
	| { type: "CONVERSATION_SELECTED"; id: string | null }
	| { type: "CONVERSATION_CREATED"; conv: TeamConversationDTO }
	// Messages
	| { type: "MESSAGES_LOADED"; messages: ChatMessage[] }
	| { type: "MESSAGES_LOAD_FAILED" }
	| { type: "MESSAGES_UPDATED"; updater: (msgs: ChatMessage[]) => ChatMessage[] }
	// Streaming
	| { type: "STREAMING_STARTED"; userMsg: ChatMessage; leaderMsg: ChatMessage }
	| { type: "STREAMING_STOPPED" }
	| { type: "STREAMING_ERROR"; leaderMsgId: string; errorMsg: string }
	// Tools
	| { type: "TOOL_STARTED"; id: string; name: string }
	| { type: "TOOL_FINISHED"; id: string }
	// Confirmation
	| { type: "CONFIRMATION_SET"; confirmation: PendingConfirmation | null }
	| { type: "RESUME_STARTED"; confirmationId: string; approved: boolean; aiMsg: ChatMessage }
	// Logs
	| { type: "LOG_ADDED"; entry: LogEntry };

function chatReducer(state: ChatState, action: ChatAction): ChatState {
	switch (action.type) {
		// ── Lifecycle ────────────────────────────────────────────
		case "RESET":
			return { ...INITIAL_STATE, loadingChat: true };

		// ── Conversations ────────────────────────────────────────
		case "CONVERSATIONS_LOADED":
			// Se há conversas, seleciona a mais recente e mantém loading
			// (as mensagens serão carregadas em seguida pelo effect)
			if (action.latestId != null) {
				return { ...state, conversations: action.data, conversationId: action.latestId };
			}
			// Sem conversas — nada mais a carregar
			return { ...state, conversations: action.data, loadingChat: false };

		case "CONVERSATIONS_LOAD_FAILED":
			return { ...state, loadingChat: false };

		case "CONVERSATION_SELECTED":
			if (action.id === state.conversationId) return state;
			return { ...state, conversationId: action.id, loadingChat: true };

		case "CONVERSATION_CREATED":
			return {
				...state,
				conversations: [action.conv, ...state.conversations],
				conversationId: action.conv.id,
				loadingChat: false,
			};

		// ── Messages ─────────────────────────────────────────────
		case "MESSAGES_LOADED":
			return { ...state, messages: action.messages, loadingChat: false };

		case "MESSAGES_LOAD_FAILED":
			return { ...state, messages: [], loadingChat: false };

		case "MESSAGES_UPDATED":
			return { ...state, messages: action.updater(state.messages) };

		// ── Streaming ────────────────────────────────────────────
		case "STREAMING_STARTED":
			return {
				...state,
				messages: [...state.messages, action.userMsg, action.leaderMsg],
				isStreaming: true,
			};

		case "STREAMING_STOPPED":
			return { ...state, isStreaming: false, activeTools: new Map() };

		case "STREAMING_ERROR":
			return {
				...state,
				messages: state.messages.map((m) =>
					m.id === action.leaderMsgId ? { ...m, thinking: undefined, error: true, errorMessage: action.errorMsg } : m,
				),
				isStreaming: false,
				activeTools: new Map(),
			};

		// ── Tools ────────────────────────────────────────────────
		case "TOOL_STARTED": {
			const tools = new Map(state.activeTools);
			tools.set(action.id, action.name);
			return { ...state, activeTools: tools };
		}

		case "TOOL_FINISHED": {
			const tools = new Map(state.activeTools);
			tools.delete(action.id);
			return { ...state, activeTools: tools };
		}

		// ── Confirmation ─────────────────────────────────────────
		case "CONFIRMATION_SET":
			return { ...state, pendingConfirmation: action.confirmation };

		case "RESUME_STARTED":
			return {
				...state,
				messages: [
					...state.messages.map((m) => {
						if (m.senderType === "confirmation" && m.metadata?.confirmationId === action.confirmationId) {
							return { ...m, metadata: { ...m.metadata, status: action.approved ? "approved" : "denied" } };
						}
						return m;
					}),
					action.aiMsg,
				],
				isStreaming: true,
			};

		// ── Logs ─────────────────────────────────────────────────
		case "LOG_ADDED":
			return { ...state, logs: [...state.logs.slice(-100), action.entry] };

		default:
			return state;
	}
}

// ── Hook ──────────────────────────────────────────────────────────

interface UseTeamMessagesOptions {
	/** ID do workspace (usado nas chamadas de API) */
	workspaceId: string | null;
	/** ID interno do workspace (usado como fallback na inscrição WebSocket) */
	workspaceInternalId?: string | number | null;
	/** ID da team (usado nas chamadas de API team-scoped) */
	teamId?: string | null;
	/** ID interno da team (usado na inscrição WebSocket) */
	teamInternalId?: string | number | null;
}

export const useTeamMessages = ({ workspaceId, workspaceInternalId, teamId, teamInternalId }: UseTeamMessagesOptions) => {
	const [state, dispatch] = useReducer(chatReducer, INITIAL_STATE);

	// ── Refs para acesso ao estado atual em callbacks estáveis ──

	const stateRef = useRef(state);
	stateRef.current = state;

	const workspaceIdRef = useRef(workspaceId);
	workspaceIdRef.current = workspaceId;

	const workspaceInternalIdRef = useRef(workspaceInternalId);
	workspaceInternalIdRef.current = workspaceInternalId;

	const teamIdRef = useRef(teamId);
	teamIdRef.current = teamId;

	const teamInternalIdRef = useRef(teamInternalId);
	teamInternalIdRef.current = teamInternalId;

	// ── Refs internos ───────────────────────────────────────────

	const wsSubscriptionRef = useRef<{ stop: () => void } | null>(null);
	const activeLeaderMsgIdRef = useRef<string | null>(null);
	const skipNextMessageLoadRef = useRef(false);
	const logsEndRef = useRef<HTMLDivElement>(null);

	// ── Stream buffer (renderização suave RNF002) ──────────────
	const streamBuffer = useStreamBuffer({
		charsPerFrame: 3,
		onFlush: (accumulated) => {
			const msgId = activeLeaderMsgIdRef.current;
			if (!msgId) return;
			dispatch({
				type: "MESSAGES_UPDATED",
				updater: (msgs) => msgs.map((m) => (m.id === msgId ? { ...m, content: accumulated, thinking: undefined } : m)),
			});
		},
	});

	// ── Helpers estáveis (nunca são recriados) ──────────────────

	const addLog = useCallback((sender: string, senderColor: string, text: string) => {
		dispatch({ type: "LOG_ADDED", entry: { time: nowTime(), sender, senderColor, text } });
	}, []);

	const cleanupWS = useCallback(() => {
		wsSubscriptionRef.current?.stop();
		wsSubscriptionRef.current = null;
	}, []);

	const stopStreaming = useCallback(() => {
		streamBuffer.flush();
		const msgId = activeLeaderMsgIdRef.current;
		if (msgId) {
			dispatch({
				type: "MESSAGES_UPDATED",
				updater: (msgs) => msgs.map((m) => (m.id === msgId ? { ...m, isStreaming: false } : m)),
			});
		}
		activeLeaderMsgIdRef.current = null;
		dispatch({ type: "STREAMING_STOPPED" });
		cleanupWS();
	}, [cleanupWS, streamBuffer]);

	// ── Effects ─────────────────────────────────────────────────

	// Reset + carregar conversas ao mudar de team
	useEffect(() => {
		if (!workspaceId) return;
		let cancelled = false;

		cleanupWS();
		dispatch({ type: "RESET" });

		const currentTeamId = teamIdRef.current ?? undefined;
		const load = async () => {
			try {
				const data = await TeamLeaderServices.listConversations(workspaceId, currentTeamId);
				if (cancelled) return;

				let latestId: string | null = null;
				if (data.length > 0) {
					latestId = [...data].sort((a, b) => new Date(b.updatedAt).getTime() - new Date(a.updatedAt).getTime())[0].id;
				}
				dispatch({ type: "CONVERSATIONS_LOADED", data, latestId });
			} catch (error) {
				if (!cancelled) {
					dispatch({ type: "CONVERSATIONS_LOAD_FAILED" });
					NotistackServices.showErrorMessage(HandleError.isHandleError(error) ? error.message : "Erro ao carregar conversas");
				}
			}
		};

		load();
		return () => {
			cancelled = true;
		};
	}, [workspaceId, cleanupWS]);

	// Carregar mensagens ao mudar de conversa
	useEffect(() => {
		if (!workspaceId || !state.conversationId) return;

		// Conversa recém-criada — não há mensagens no servidor ainda
		if (skipNextMessageLoadRef.current) {
			skipNextMessageLoadRef.current = false;
			return;
		}

		let cancelled = false;

		const load = async () => {
			try {
				const result = await TeamLeaderServices.getConversation(workspaceId, state.conversationId!, 100, 0, teamIdRef.current ?? undefined);
				if (cancelled) return;
				dispatch({ type: "MESSAGES_LOADED", messages: result.messages.map(mapMessageDTO).filter((m): m is ChatMessage => m !== null) });
			} catch {
				if (!cancelled) dispatch({ type: "MESSAGES_LOAD_FAILED" });
			}
		};

		load();
		return () => {
			cancelled = true;
		};
	}, [workspaceId, state.conversationId]);

	// Cleanup WS ao desmontar
	useEffect(() => () => cleanupWS(), [cleanupWS]);

	// Auto-scroll dos logs
	useEffect(() => {
		logsEndRef.current?.scrollIntoView({ behavior: "smooth" });
	}, [state.logs]);

	// ── Inscrição WebSocket (interno) ───────────────────────────

	const subscribeToWS = useCallback(
		(convId: string, leaderMsgId: string) => {
			const currentTeamId = teamInternalIdRef.current;
			const currentWorkspaceId = workspaceInternalIdRef.current;
			const wsId = currentTeamId ?? currentWorkspaceId;
			if (wsId == null) return;
			cleanupWS();
			activeLeaderMsgIdRef.current = leaderMsgId;

			/** Atualiza somente a mensagem do líder referente a este streaming */
			const updateLeaderMsg = (updater: (msg: ChatMessage) => ChatMessage) => {
				dispatch({ type: "MESSAGES_UPDATED", updater: (msgs) => msgs.map((m) => (m.id === leaderMsgId ? updater(m) : m)) });
			};

			const callbacks: StreamCallbacks = {
				onProcessingStarted: () => {
					updateLeaderMsg((m) => ({ ...m, thinking: "Processando..." }));
					addLog("Líder IA", "#4ade80", "Processamento iniciado.");
				},

				onToken: (data) => {
					const token = data.token || data.content || "";
					if (token) streamBuffer.push(token);
				},

				onToolCall: (data: SSEToolCall) => {
					dispatch({ type: "TOOL_STARTED", id: data.id, name: data.name });
					updateLeaderMsg((m) => ({ ...m, thinking: m.content ? undefined : `Executando: ${data.name}...` }));
					addLog("Líder IA", "#4ade80", `Chamando ferramenta: ${data.name}`);
				},

				onToolResult: (data) => {
					dispatch({ type: "TOOL_FINISHED", id: data.id });
					addLog(data.name, "#f59e0b", "Ferramenta finalizada.");
				},

				onConfirmationRequired: (data: SSEConfirmationRequired) => {
					// Legacy: ainda pode ser emitido durante o tool loop (estado intermediário)
					updateLeaderMsg((m) => ({ ...m, thinking: m.content ? undefined : "Aguardando sua confirmação..." }));
					addLog("System", "#60a5fa", `Confirmação necessária: ${data.toolName}`);
				},

				onConfirmationMessage: (data: SSEConfirmationMessage) => {
					// Confirmação salva no banco — adicionar como mensagem inline no chat
					const msg = data.message;
					const content = extractTextFromMessage(msg) || "Deseja executar esta ferramenta?";
					const senderRef = isLegacyAssistantMessageDTO(msg) ? (msg.senderRef ?? undefined) : undefined;
					dispatch({
						type: "MESSAGES_UPDATED",
						updater: (msgs) => {
							// Remover placeholder do líder se estiver vazio
							const filtered = msgs.filter((m) => !(m.id === leaderMsgId && !m.content));
							return [
								...filtered,
								{
									id: `msg-${msg.id}`,
									content,
									senderType: "confirmation" as const,
									senderRef,
									createdAt: msg.createdAt,
									metadata: msg.metadata ?? {},
								},
							];
						},
					});
					addLog("System", "#60a5fa", `Confirmação pendente: ${msg.metadata?.toolCall?.name ?? "ferramenta"}`);
				},

				onConfirmationResolved: () => {
					// Legacy — pode ser emitido pelo backend antigo, só atualiza UI
					updateLeaderMsg((m) => ({ ...m, thinking: m.content ? undefined : "Continuando..." }));
				},

				onComplete: (data) => {
					streamBuffer.flush();
					updateLeaderMsg((m) => ({ ...m, content: data.content || m.content, thinking: undefined }));
				},

				onMessageSaved: (data) => {
					const savedAiMsg = data.leaderMessage ?? data.assistantMessage;
					dispatch({
						type: "MESSAGES_UPDATED",
						updater: (msgs) =>
							msgs.map((m) => {
								// Atualiza a mensagem otimista do usuário
								if (data.userMessage && m.senderType === "user" && m.id.startsWith("user-")) {
									return {
										...m,
										id: `msg-${data.userMessage.id}`,
										content: extractTextFromMessage(data.userMessage),
										createdAt: data.userMessage.createdAt,
									};
								}
								// Atualiza a mensagem placeholder do líder
								if (savedAiMsg && m.id === leaderMsgId) {
									activeLeaderMsgIdRef.current = `msg-${savedAiMsg.id}`;
									return {
										...m,
										id: `msg-${savedAiMsg.id}`,
										content: extractTextFromMessage(savedAiMsg) || m.content,
										thinking: undefined,
										createdAt: savedAiMsg.createdAt,
										isStreaming: m.isStreaming,
									};
								}
								return m;
							}),
					});
					if (savedAiMsg) addLog("Líder IA", "#4ade80", "Mensagem salva com sucesso.");
				},

				onProcessingStatus: (data) => {
					if (data.currentStep) {
						updateLeaderMsg((m) => (!m.content ? { ...m, thinking: data.currentStep } : m));
						addLog("System", "#60a5fa", `Status: ${data.currentStep}`);
					}
				},

				onProcessingCompleted: () => {
					stopStreaming();
					addLog("System", "#60a5fa", "Processamento concluído.");
				},

				onProcessingError: (data) => {
					const errorMsg = data.error || "Erro desconhecido no processamento.";
					dispatch({ type: "STREAMING_ERROR", leaderMsgId, errorMsg });
					cleanupWS();
					addLog("System", "#ef4444", `Erro: ${errorMsg}`);
					NotistackServices.showErrorMessage(errorMsg);
				},

				onDone: () => stopStreaming(),

				onError: (data) => {
					const errorMsg = data?.message || "Ocorreu um erro inesperado.";
					dispatch({ type: "STREAMING_ERROR", leaderMsgId, errorMsg });
					cleanupWS();
					addLog("System", "#ef4444", `Erro: ${errorMsg}`);
					NotistackServices.showErrorMessage(errorMsg);
				},
			};

			wsSubscriptionRef.current = TeamLeaderServices.subscribeToConversation(wsId, convId, callbacks);
		},
		[addLog, cleanupWS, stopStreaming, streamBuffer],
	);

	// ── Garantir que conversa existe ────────────────────────────

	const ensureConversation = useCallback(async (): Promise<string> => {
		const currentConvId = stateRef.current.conversationId;
		if (currentConvId) return currentConvId;

		const currentWorkspaceId = workspaceIdRef.current;
		if (!currentWorkspaceId) throw new Error("Nenhum workspace selecionado");

		const conv = await TeamLeaderServices.createConversation(currentWorkspaceId, "Nova conversa", teamIdRef.current ?? undefined);
		skipNextMessageLoadRef.current = true;
		dispatch({ type: "CONVERSATION_CREATED", conv });
		addLog("System", "#60a5fa", `Conversa criada: ${conv.id}`);
		return conv.id;
	}, [addLog]);

	// ── Enviar mensagem ─────────────────────────────────────────

	const sendMessage = useCallback(
		async (content: string, files?: File[]) => {
			const trimmed = content.trim();
			const currentWorkspaceId = workspaceIdRef.current;
			if (stateRef.current.isStreaming || (!trimmed && !files?.length) || !currentWorkspaceId) return;

			let attachments: MessageAttachment[] | undefined;
			if (files?.length) {
				try {
					attachments = await filesToAttachments(currentWorkspaceId, files);
				} catch (err: any) {
					NotistackServices.showErrorMessage(err?.message || "Falha ao subir um ou mais arquivos.");
					return;
				}
			}

			const now = new Date().toISOString();
			const leaderMsgId = `leader-${Date.now()}`;
			const userMsgContent = trimmed || (files?.length ? files.map((f) => `📎 ${f.name}`).join("\n") : "");
			const userMsg: ChatMessage = { id: `user-${Date.now()}`, content: userMsgContent, senderType: "user", createdAt: now };
			const leaderMsg: ChatMessage = {
				id: leaderMsgId,
				content: "",
				senderType: "leader",
				thinking: "Pensando...",
				createdAt: now,
				isStreaming: true,
			};

			dispatch({ type: "STREAMING_STARTED", userMsg, leaderMsg });
			streamBuffer.reset();
			addLog("User", "#4ade80", `Comando enviado: "${trimmed.slice(0, 60)}${trimmed.length > 60 ? "..." : ""}"`);

			try {
				const convId = await ensureConversation();
				subscribeToWS(convId, leaderMsgId);
				await TeamLeaderServices.sendMessage(currentWorkspaceId, convId, trimmed, teamIdRef.current ?? undefined, attachments);
			} catch (error: any) {
				const errorMsg = HandleError.isHandleError(error) ? error.message : error?.message || "Não foi possível enviar a mensagem.";
				dispatch({ type: "STREAMING_ERROR", leaderMsgId, errorMsg });
				cleanupWS();
				addLog("System", "#ef4444", `Erro ao enviar: ${errorMsg}`);
				NotistackServices.showErrorMessage(errorMsg);
			}
		},
		[ensureConversation, subscribeToWS, addLog, cleanupWS, streamBuffer],
	);

	// ── Confirmar/negar ferramenta (inline no chat) ────────────

	const handleConfirmation = useCallback(
		async (confirmationId: string, approved: boolean, reason?: string) => {
			const currentWorkspaceId = workspaceIdRef.current;
			const convId = stateRef.current.conversationId;
			if (!currentWorkspaceId || !convId || stateRef.current.isStreaming) return;

			const leaderMsgId = `leader-${Date.now()}`;

			// Marcar confirmação como respondida + adicionar placeholder do líder
			dispatch({
				type: "RESUME_STARTED",
				confirmationId,
				approved,
				aiMsg: {
					id: leaderMsgId,
					content: "",
					senderType: "leader",
					thinking: approved ? "Confirmação aprovada, executando..." : "Negado, retomando...",
					createdAt: new Date().toISOString(),
					isStreaming: true,
				},
			});

			streamBuffer.reset();
			addLog("User", "#4ade80", `Ferramenta ${approved ? "aprovada" : "negada"}`);

			// Inscrever-se no WS antes de chamar a API (para não perder eventos)
			subscribeToWS(convId, leaderMsgId);

			try {
				await TeamLeaderServices.confirmToolExecution(currentWorkspaceId, confirmationId, approved, reason, teamIdRef.current ?? undefined);
			} catch (error: any) {
				const errorMsg = error?.message || "Erro ao processar confirmação.";
				dispatch({ type: "STREAMING_ERROR", leaderMsgId, errorMsg });
				cleanupWS();
				NotistackServices.showErrorMessage(errorMsg);
			}
		},
		[addLog, subscribeToWS, cleanupWS, streamBuffer],
	);

	// ── Seletor de conversa (exposto ao consumidor) ─────────────

	const setConversationId = useCallback((id: string | null) => {
		dispatch({ type: "CONVERSATION_SELECTED", id });
	}, []);

	// ── API pública ─────────────────────────────────────────────

	return {
		// Conversations
		conversations: state.conversations,
		conversationId: state.conversationId,
		setConversationId,

		// Messages
		messages: state.messages,
		isStreaming: state.isStreaming,
		loadingChat: state.loadingChat,

		// Tools & Confirmations
		activeTools: state.activeTools,
		pendingConfirmation: state.pendingConfirmation,

		// Logs
		logs: state.logs,
		logsEndRef,
		addLog,

		// Actions
		sendMessage,
		handleConfirmation,
	};
};
